/**
 * WOAdvancedSettings - SLA targets, Templates, Webhooks for Work Orders.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Chip, IconButton, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
  Switch, FormControlLabel,
} from "@mui/material";
import AddIcon      from "@mui/icons-material/Add";
import DeleteIcon    from "@mui/icons-material/Delete";
import EditIcon      from "@mui/icons-material/Edit";
import SaveIcon      from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import api from "../../utils/api";

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

// ── SLA per type ───────────────────────────────────────────────────────────
const WOSLASettings = () => {
  const [types,  setTypes]  = useState([]);
  const [saving, setSaving] = useState({});
  const [snack,  setSnack]  = useState({ open:false, msg:"", sev:"success" });

  useEffect(() => { api.get("/workorder-types").then(r => setTypes(r.data||[])).catch(()=>{}); }, []);

  const handleSave = async (type) => {
    setSaving(p => ({...p,[type.id]:true}));
    try {
      await api.put(`/workorder-types/${type.id}/sla`, { sla_days: type.sla_days });
      setSnack({ open:true, msg:`SLA updated for "${type.type}".`, sev:"success" });
    } catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
    finally { setSaving(p => ({...p,[type.id]:false})); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Set target days to resolution for each work order type. Requests exceeding 80% will show a warning.
      </Typography>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>{["Type","SLA (days)","Action"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {types.map(t => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{t.type}</TableCell>
                <TableCell>
                  <TextField type="number" size="small" value={t.sla_days||""} placeholder="No SLA"
                    onChange={e => setTypes(prev => prev.map(x => x.id===t.id ? {...x,sla_days:e.target.value?parseInt(e.target.value,10):null}:x))}
                    inputProps={{ min:1, max:365 }} sx={{ ...fieldSx, width:120 }} />
                </TableCell>
                <TableCell>
                  <Button size="small" startIcon={saving[t.id]?<CircularProgress size={12}/>:<SaveIcon fontSize="small"/>}
                    onClick={()=>handleSave(t)} sx={{ ...btnSx, py:0.5 }}>Save</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Templates ──────────────────────────────────────────────────────────────
const WOTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [types,     setTypes]     = useState([]);
  const [subtypes,  setSubtypes]  = useState([]);
  const [locations, setLocations] = useState([]);
  const [form,      setForm]      = useState({ name:"", description:"", workorder_type_id:"", workorder_subtype_id:"", location_id:"", default_description:"", default_details:"", default_priority:"Low" });
  const [editing,   setEditing]   = useState(null);
  const [snack,     setSnack]     = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    const [tr, ty] = await Promise.all([api.get("/workorder-templates"), api.get("/workorder-types")]);
    setTemplates(tr.data||[]); setTypes(ty.data||[]);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!form.workorder_type_id) { setSubtypes([]); setLocations([]); return; }
    Promise.all([
      api.get(`/workorder-subtypes-by-type?type_id=${form.workorder_type_id}`),
      api.get(`/workorder-locations-by-type?type_id=${form.workorder_type_id}`)
    ]).then(([s,l]) => { setSubtypes(s.data||[]); setLocations(l.data||[]); }).catch(()=>{});
  }, [form.workorder_type_id]);

  const handleSave = async () => {
    if (!form.name) { setSnack({ open:true, msg:"Name required.", sev:"error" }); return; }
    try {
      if (editing) await api.put(`/workorder-templates/${editing}`, form);
      else         await api.post("/workorder-templates", form);
      setForm({ name:"", description:"", workorder_type_id:"", workorder_subtype_id:"", location_id:"", default_description:"", default_details:"", default_priority:"Low" });
      setEditing(null); setSnack({ open:true, msg:"Saved.", sev:"success" }); load();
    } catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };

  return (
    <Box>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>{editing?"Edit Template":"Add Template"}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Template Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField select fullWidth size="small" label="Priority" value={form.default_priority} onChange={e=>setForm(f=>({...f,default_priority:e.target.value}))} sx={fieldSx}>{["Low","Medium","High","Critical"].map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Type" value={form.workorder_type_id} onChange={e=>setForm(f=>({...f,workorder_type_id:e.target.value,workorder_subtype_id:"",location_id:""}))} sx={fieldSx}><MenuItem value="">Any</MenuItem>{types.map(t=><MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Subtype" value={form.workorder_subtype_id} disabled={!subtypes.length} onChange={e=>setForm(f=>({...f,workorder_subtype_id:e.target.value}))} sx={fieldSx}><MenuItem value="">Any</MenuItem>{subtypes.map(s=><MenuItem key={s.id} value={s.id}>{s.subtype_name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Location" value={form.location_id} disabled={!locations.length} onChange={e=>setForm(f=>({...f,location_id:e.target.value}))} sx={fieldSx}><MenuItem value="">Any</MenuItem>{locations.map(l=><MenuItem key={l.id} value={l.id}>{l.location_name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Default Description" value={form.default_description} onChange={e=>setForm(f=>({...f,default_description:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Default Details" value={form.default_details} onChange={e=>setForm(f=>({...f,default_details:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}>
            <Box sx={{ display:"flex", gap:1 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={btnSx}>{editing?"Update":"Save Template"}</Button>
              {editing && <Button variant="outlined" onClick={()=>{ setEditing(null); setForm({ name:"", description:"", workorder_type_id:"", workorder_subtype_id:"", location_id:"", default_description:"", default_details:"", default_priority:"Low" }); }} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["Name","Type","Priority",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {templates.map(t=>(
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{t.name}</TableCell>
                <TableCell sx={{ fontSize:12 }}>{t.type_name||"Any"}</TableCell>
                <TableCell><Chip size="small" label={t.default_priority||"Low"} sx={{ fontSize:10, height:18, borderRadius:"6px" }} /></TableCell>
                <TableCell>
                  <Box sx={{ display:"flex", gap:0.5 }}>
                    <IconButton size="small" onClick={()=>{ setEditing(t.id); setForm({ name:t.name, description:t.description||"", workorder_type_id:t.workorder_type_id||"", workorder_subtype_id:t.workorder_subtype_id||"", location_id:t.location_id||"", default_description:t.default_description||"", default_details:t.default_details||"", default_priority:t.default_priority||"Low" }); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/workorder-templates/${t.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!templates.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No templates.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Webhooks ───────────────────────────────────────────────────────────────
const WOWebhooks = () => {
  const [hooks, setHooks] = useState([]);
  const [form,  setForm]  = useState({ url:"", events:"status_change,comment_added", secret_key:"" });
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });
  const load = useCallback(async () => { const r = await api.get("/wo-webhooks"); setHooks(r.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const handleAdd = async () => {
    if (!form.url) { setSnack({ open:true, msg:"URL required.", sev:"error" }); return; }
    try { await api.post("/wo-webhooks", form); setForm({ url:"", events:"status_change,comment_added", secret_key:"" }); setSnack({ open:true, msg:"Added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };
  return (
    <Box>
      <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>Receive HTTP POST notifications when work order events occur.</Alert>
      <Paper elevation={0} sx={cardSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Endpoint URL" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Events" value={form.events} onChange={e=>setForm(f=>({...f,events:e.target.value}))} sx={fieldSx} helperText="status_change, comment_added, *" /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Secret" value={form.secret_key} onChange={e=>setForm(f=>({...f,secret_key:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add Webhook</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["URL","Events","Active",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {hooks.map(h=>(
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontSize:12, maxWidth:280 }}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:260 }}>{h.url}</Typography></TableCell>
                <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{h.events}</TableCell>
                <TableCell><Chip size="small" label={h.is_active?"Active":"Off"} onClick={async()=>{ await api.patch(`/wo-webhooks/${h.id}`,{is_active:!h.is_active}); load(); }} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:h.is_active?"rgba(34,197,94,0.1)":"rgba(0,0,0,0.06)", color:h.is_active?"#16a34a":"#6b778c", cursor:"pointer" }} /></TableCell>
                <TableCell>
                  <Box sx={{ display:"flex", gap:0.5 }}>
                    <IconButton size="small" color="primary" title="Send test ping"
                      onClick={async()=>{
                        try {
                          await fetch(h.url, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ event:"test", timestamp: new Date().toISOString(), source:"MyHOA" }) });
                          setSnack({ open:true, msg:`Test ping sent to ${h.url}`, sev:"success" });
                        } catch { setSnack({ open:true, msg:"Test ping failed (check URL / CORS).", sev:"error" }); }
                      }}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/wo-webhooks/${h.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!hooks.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No webhooks.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────
const WOAdvancedSettings = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Advanced Settings</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>SLA targets, work order templates, and webhook integrations.</Typography>
      </Box>
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:44, fontSize:13 } }}>
          <Tab label="SLA Targets" />
          <Tab label="WO Templates" />
          <Tab label="Webhooks" />
        </Tabs>
      </Paper>
      {tab===0 && <WOSLASettings />}
      {tab===1 && <WOTemplatesManager />}
      {tab===2 && <WOWebhooks />}
    </Box>
  );
};

export default WOAdvancedSettings;
